// CuratedGalleryModal — full-screen modal that surfaces a property's
// Curated Gallery from anywhere in the app (property cards, listings,
// etc.) without requiring the guest to navigate to the /hotels/:slug
// page first.
//
// Data source: `useCuratedGallery(hotelSlug)` — the same public
// endpoint the section on the hotel detail page uses. Fetch only fires
// when the modal actually mounts (i.e. when `open` transitions to true)
// so cards on a list page don't pay the N × network cost upfront.
//
// UX
// ──
// • Backdrop + Escape + close-button all dismiss.
// • Grid (2/3/5 cols responsive) mirrors the on-page section so guests
//   feel continuity between surfaces.
// • Clicking a tile opens the in-modal Lightbox with ←/→ + thumbnail
//   strip navigation.
// • Empty / loading / error states have their own polite copy — the
//   button on the property card should already hide when nothing is
//   available, but the modal still handles the race gracefully.

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Images, Loader2, X } from "lucide-react";
import useCuratedGallery from "@/lib/useCuratedGallery";

const t = (lang, en, fr, es) => (lang === "fr" ? fr : lang === "es" ? es : en);

const CuratedGalleryModal = ({ open, onClose, hotelSlug, hotelName, lang = "en" }) => {
  const { images, loading } = useCuratedGallery(open ? hotelSlug : null);
  const [idx, setIdx] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (idx !== null) setIdx(null);
        else onClose?.();
      }
      if (e.key === "ArrowRight" && idx !== null && images.length > 0) {
        setIdx((i) => (i + 1) % images.length);
      }
      if (e.key === "ArrowLeft" && idx !== null && images.length > 0) {
        setIdx((i) => (i - 1 + images.length) % images.length);
      }
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, idx, images.length, onClose]);

  // Reset lightbox index when the modal closes so re-opening starts fresh.
  useEffect(() => {
    if (!open) setIdx(null);
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  const title = hotelName || t(lang, "Photo gallery", "Galerie photos", "Galería");
  const overline = t(lang, "GALLERY", "GALERIE", "GALERÍA");
  const closeLabel = t(lang, "Close", "Fermer", "Cerrar");
  const emptyMsg = t(
    lang,
    "No curated photos are available yet for this property.",
    "Aucune photo n'est encore disponible pour cet établissement.",
    "Aún no hay fotos disponibles para este alojamiento."
  );
  const loadingMsg = t(lang, "Loading gallery…", "Chargement…", "Cargando galería…");
  const counter = idx !== null
    ? `${String(idx + 1).padStart(2, "0")} / ${String(images.length).padStart(2, "0")}`
    : "";

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-[#1A1513]/95 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="curated-gallery-modal"
    >
      {/* Header */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between px-4 md:px-10 py-4 md:py-5 border-b border-[#FDFBF7]/10">
        <div className="min-w-0">
          <p className="text-[10px] md:text-[11px] tracking-[0.28em] uppercase text-[#D4A373]">
            {overline}
          </p>
          <p className="font-serif-x text-lg md:text-2xl text-[#FDFBF7] leading-tight truncate">
            {title}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          className="shrink-0 inline-flex items-center justify-center w-10 h-10 border border-[#FDFBF7]/25 text-[#FDFBF7]/85 hover:border-[#D4A373] hover:text-[#D4A373] transition-colors"
          data-testid="curated-gallery-modal-close"
        >
          <X className="w-4 h-4" strokeWidth={1.6} />
        </button>
      </div>

      {/* Body */}
      <div className="absolute inset-0 pt-24 md:pt-28 pb-6 md:pb-10 px-4 md:px-10 overflow-y-auto">
        {loading ? (
          <div className="h-full flex items-center justify-center text-[#FDFBF7]/70 gap-2">
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.7} />
            <span className="text-[11px] tracking-[0.28em] uppercase">{loadingMsg}</span>
          </div>
        ) : images.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center text-[#FDFBF7]/70 gap-3">
            <Images className="w-7 h-7" strokeWidth={1.4} />
            <p className="text-sm max-w-md">{emptyMsg}</p>
          </div>
        ) : (
          <div
            className="max-w-7xl mx-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-3"
            data-testid="curated-gallery-modal-grid"
          >
            {images.map((image, i) => (
              <button
                type="button"
                key={image.id}
                onClick={() => setIdx(i)}
                className="group relative aspect-square overflow-hidden bg-[#2C2621] focus:outline-none focus:ring-2 focus:ring-[#D4A373]"
                data-testid={`curated-gallery-modal-tile-${i}`}
                aria-label={image.name || `Photo ${i + 1}`}
              >
                <img
                  src={image.url}
                  alt={image.name || title}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                  onError={(e) => {
                    e.currentTarget.style.opacity = "0.35";
                  }}
                />
                <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox — nested full-screen overlay for a single image. */}
      {idx !== null && images[idx] ? (
        <div
          className="absolute inset-0 z-10 bg-[#0E0A08]/97 flex items-center justify-center px-3 md:px-12"
          data-testid="curated-gallery-modal-lightbox"
        >
          <button
            type="button"
            onClick={() => setIdx(null)}
            aria-label={closeLabel}
            className="absolute top-4 right-4 md:top-6 md:right-6 text-[#FDFBF7]/80 hover:text-[#FDFBF7] p-2"
            data-testid="curated-gallery-modal-lightbox-close"
          >
            <X className="w-6 h-6" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={() => setIdx((i) => (i - 1 + images.length) % images.length)}
            aria-label="Previous"
            className="absolute left-1 md:left-8 top-1/2 -translate-y-1/2 text-[#FDFBF7]/70 hover:text-[#FDFBF7] p-3"
            data-testid="curated-gallery-modal-lightbox-prev"
          >
            <ChevronLeft className="w-7 h-7 md:w-8 md:h-8" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={() => setIdx((i) => (i + 1) % images.length)}
            aria-label="Next"
            className="absolute right-1 md:right-8 top-1/2 -translate-y-1/2 text-[#FDFBF7]/70 hover:text-[#FDFBF7] p-3"
            data-testid="curated-gallery-modal-lightbox-next"
          >
            <ChevronRight className="w-7 h-7 md:w-8 md:h-8" strokeWidth={1.5} />
          </button>
          <div className="max-w-6xl w-full">
            <img
              src={images[idx].url}
              alt={images[idx].name || title}
              loading="lazy"
              decoding="async"
              className="w-full max-h-[72vh] md:max-h-[78vh] object-contain"
              data-testid="curated-gallery-modal-lightbox-image"
            />
            <div className="mt-4 md:mt-6 flex items-center justify-between text-[#FDFBF7]/85">
              <p className="font-serif-x text-lg md:text-2xl truncate max-w-[70%]">{title}</p>
              <p className="text-[10px] md:text-xs tracking-[0.25em] uppercase tabular-nums">{counter}</p>
            </div>
            <div className="mt-3 md:mt-4 flex gap-1.5 md:gap-2 overflow-x-auto pb-1">
              {images.map((im, i) => (
                <button
                  key={im.id}
                  type="button"
                  onClick={() => setIdx(i)}
                  className={`shrink-0 w-16 h-12 md:w-20 md:h-14 overflow-hidden border-2 transition-opacity ${
                    i === idx
                      ? "border-[#D4A373] opacity-100"
                      : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                  aria-label={`Photo ${i + 1}`}
                >
                  <img
                    src={im.url}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>,
    document.body
  );
};

export default CuratedGalleryModal;
